import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import TraceTimeline from './TraceTimeline';
import TraceFilters from './TraceFilters';
import EntityLink from '../../components/Common/EntityLink';
import './OrderTracer.css';
import { formatAppDate, localToday } from '../../utils/dateUtils';

// IN_PROGRESS -> inProgress (keys of production.json → status)
const statusKey = (code) => code.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const OrderTracer = forwardRef((props, ref) => {
  const { t } = useTranslation('supervisor');
  const { t: tProd } = useTranslation('production');
  // Production order status label in the UI language; the code is the fallback (#386)
  const statusLabel = (code) => (code ? tProd(`status.${statusKey(code)}`, { defaultValue: code }) : '');
  const { user } = useAuth();
  const location = useLocation();
  const { showSuccess, showError } = useMessages();

  // State management
  const [productionOrders, setProductionOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [traceData, setTraceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [filters, setFilters] = useState({
    eventTypes: [],
    dateRange: { start: '', end: '' },
    searchTerm: ''
  });

  // Expose refresh method to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchProductionOrdersList();
      // Also refresh current trace data if available
      if (selectedOrderId) {
        fetchTraceData(selectedOrderId);
      }
    }
  }));

  // Fetch production orders list on component mount
  useEffect(() => {
    fetchProductionOrdersList();
  }, []);

  const fetchProductionOrdersList = async () => {
    setLoadingOrders(true);
    try {
      const response = await apiFetch(getApiUrl('/api/production/orders/list'));

      if (response.ok) {
        const data = await response.json();
        setProductionOrders(data.orders || []);
      } else {
        throw new Error('Failed to fetch production orders');
      }
    } catch (err) {
      console.error('Error fetching production orders:', err);
      showError(t('orderTracer.messages.error'), t('orderTracer.messages.failedToLoadOrders'));
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchTraceData = async (productionOrderId) => {
    if (!productionOrderId) return;

    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/production/orders/${productionOrderId}/trace`));

      if (response.ok) {
        const data = await response.json();
        setTraceData(data);
        showSuccess(t('orderTracer.messages.success'), t('orderTracer.messages.loadedEvents', { count: data.total_events }));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch trace data');
      }
    } catch (err) {
      console.error('Error fetching trace data:', err);
      showError(t('orderTracer.messages.error'), err.message || t('orderTracer.messages.failedToLoadTrace'));
      setTraceData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderSelect = (event) => {
    const orderId = event.target.value;
    setSelectedOrderId(orderId);

    if (orderId) {
      fetchTraceData(orderId);
    } else {
      setTraceData(null);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExport = () => {
    if (!traceData) {
      showError(t('orderTracer.messages.error'), t('orderTracer.messages.noDataToExport'));
      return;
    }

    try {
      // Create export data
      const exportData = {
        production_order: traceData.production_order,
        events: traceData.events,
        exported_at: new Date().toISOString(),
        exported_by: user?.username || 'Unknown'
      };

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `production_order_trace_${traceData.production_order.po_number}_${localToday()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess(t('orderTracer.messages.success'), t('orderTracer.messages.exportSuccess'));
    } catch (err) {
      console.error('Error exporting trace data:', err);
      showError(t('orderTracer.messages.error'), t('orderTracer.messages.exportFailed'));
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatAppDate(dateString);
  };

  return (
    <div className="order-tracer">
      {traceData && (
        <div className="order-tracer-header">
          <div className="header-actions">
            <button
              className="btn btn-export"
              onClick={handleExport}
              title={t('orderTracer.buttons.exportTitle')}
            >
              📥 {t('orderTracer.buttons.export')}
            </button>
          </div>
        </div>
      )}

      {/* Production Order Selection */}
      <div className="order-selection-section">
        <div className="selection-header">
          <label htmlFor="order-select">{t('orderTracer.labels.selectProductionOrder')}:</label>
          {selectedOrderId && traceData && (
            <div className="order-info">
              <span className="order-number">{traceData.production_order.po_number}</span>
              <span className="customer-name">{traceData.production_order.customer_name}</span>
              <span className="order-status">{statusLabel(traceData.production_order.status)}</span>
            </div>
          )}
        </div>

        <select
          id="order-select"
          value={selectedOrderId}
          onChange={handleOrderSelect}
          disabled={loadingOrders}
          className="order-select"
        >
          <option value="" disabled hidden>{t('orderTracer.labels.chooseOrder')}...</option>
          {productionOrders.map(order => (
            <option key={order.production_order_id} value={order.production_order_id}>
              {order.po_number} - {order.customer_name} ({statusLabel(order.status)}) - {formatDate(order.created_on)}
            </option>
          ))}
        </select>

        {loadingOrders && (
          <div className="loading-orders">{t('orderTracer.messages.loadingOrders')}...</div>
        )}
      </div>


      {/* Loading State */}
      {loading && (
        <div className="loading-section">
          <div className="loading-spinner"></div>
          <div className="loading-text">{t('orderTracer.messages.loadingTrace')}...</div>
        </div>
      )}

      {/* No Data State */}
      {!loading && !traceData && selectedOrderId && (
        <div className="no-data-section">
          <div className="no-data-icon">📋</div>
          <div className="no-data-text">{t('orderTracer.messages.noTraceData')}</div>
        </div>
      )}

      {/* Trace Timeline */}
      {!loading && traceData && (
        <div className="trace-content">
          <div className="trace-summary">
            <div className="summary-card">
              <div className="summary-label">{t('orderTracer.summary.totalEvents')}</div>
              <div className="summary-value">{traceData.total_events}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('orderTracer.summary.productionOrder')}</div>
              <div className="summary-value">
                <EntityLink
                  type="production-order"
                  id={traceData.production_order.po_number}
                  label={traceData.production_order.po_number}
                  returnLabel={t('navigation.subtabs.orderTracer')}
                  returnPath={`${location.pathname}${location.search}`}
                />
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('orderTracer.summary.customer')}</div>
              <div className="summary-value">{traceData.production_order.customer_name}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">{t('orderTracer.summary.status')}</div>
              <div className={`summary-value status-badge status-${traceData.production_order.status?.toLowerCase()}`}>
                {statusLabel(traceData.production_order.status)}
              </div>
            </div>
          </div>

          <TraceTimeline
            events={traceData.events}
            productionOrder={traceData.production_order}
            filters={filters}
          />
        </div>
      )}

      {/* Welcome State */}
      {!loading && !selectedOrderId && (
        <div className="welcome-section">
          <div className="welcome-icon">🔍</div>
          <div className="welcome-title">{t('orderTracer.welcome.title')}</div>
          <div className="welcome-text">
            {t('orderTracer.welcome.description')}
            <br />
            {t('orderTracer.welcome.subtitle')}
          </div>
          <div className="welcome-features">
            <div className="feature-item">
              <span className="feature-icon">📈</span>
              <span className="feature-text">{t('orderTracer.welcome.features.quoteEvents')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🏭</span>
              <span className="feature-text">{t('orderTracer.welcome.features.productionEvents')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📦</span>
              <span className="feature-text">{t('orderTracer.welcome.features.materialEvents')}</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🚚</span>
              <span className="feature-text">{t('orderTracer.welcome.features.fulfillmentEvents')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

OrderTracer.displayName = 'OrderTracer';

export default OrderTracer;